import React, { useCallback, useContext, useRef } from 'react';
import { Tooltip } from '@/components/ui/Tooltip';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { SearchContext } from '../../features/project/building/Building';
import { Building16Regular, People16Regular, BranchFork16Regular } from '@fluentui/react-icons';
import { useTypemarkContext } from '../../features/project/typemark/TypemarkContext';
import { Checkbox } from '../ui/Checkbox';
import { cn } from '../../utils/cn';

interface DataItem {
  id: string | number;
  title: string;
  status?: string;
  classifications?: string[];
  status_label?: string;
  is_active?: boolean;
  is_working?: boolean;
}

interface NavListCustomInfiniteProps {
  makeActive: (id: string | number) => void;
  data: DataItem[];
  active: string | number;
  fetchMoreData: () => void;
  hasMore: boolean;
  loading: boolean;
  isFetching?: boolean;
  height?: number;
  dataLength?: any;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'border-blue-400 text-blue-600',
  in_progress: 'border-amber-400 text-amber-600',
  done: 'border-green-400 text-green-600',
  checked: 'border-emerald-500 text-emerald-700',
  postponed: 'border-neutral-300 text-neutral-500',
  needs_edit: 'border-red-400 text-red-600',
};

const StatusBadge = ({ item }: { item: DataItem }) => {
  const colorClasses =
    (item.status && STATUS_COLORS[item.status]) || 'border-primary-500 text-primary-500';

  return (
    <>
      {item.status_label && (
        <span
          className={cn(
            'shrink-0 ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border leading-none',
            colorClasses
          )}
        >
          {item.status_label}
        </span>
      )}
    </>
  );
};

const NavListCustomInfinite = ({
  makeActive,
  data,
  active,
  fetchMoreData,
  hasMore,
  loading,
  isFetching,
}: NavListCustomInfiniteProps) => {
  const observer = useRef<IntersectionObserver>();
  const context = useTypemarkContext() || useContext(SearchContext);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMoreData();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const handleToggleSelected = (id: string | number) => {
    if (!context) return;
    if (context.selected[id]) {
      const old = { ...context.selected };
      delete old[id];
      context.setSelected?.(old);
    } else {
      context.setSelected?.((selected: Record<string | number, string | number>) => ({
        ...selected,
        [id]: id,
      }));
    }
  };

  const handleClick = (e: React.MouseEvent, index: number) => {
    if (!context) return;
    if (e.shiftKey) {
      const last = Object.keys(context.selected)[Object.keys(context.selected).length - 1];
      const lastItemIndex = data.findIndex((item) => item.id === last);

      let newSelected: Record<string | number, string | number> = {};

      if (index >= lastItemIndex) {
        for (let i = lastItemIndex + 1; i <= index; i++) {
          newSelected = { ...newSelected, [data[i].id]: data[i].id };
        }
      } else {
        for (let i = index; i < lastItemIndex; i++) {
          newSelected = { ...newSelected, [data[i].id]: data[i].id };
        }
      }

      context.setSelected?.((selected: Record<string | number, string | number>) => ({
        ...selected,
        ...newSelected,
      }));
    }
  };

  const _makeActive = (id: string | number) => {
    makeActive(id);
    context?.setSelected?.({ [id]: id });
  };

  const renderItem = (item: DataItem, i: number, isLast: boolean) => {
    const isCompany = item.status === 'COMPANY';
    const isCommunity = item.status === 'COMMUNITY';
    const isActive = item.id === active || !!context?.selected?.[item.id];
    const isInactive = item.is_active === false && !isActive;

    return (
      <div
        key={item.id}
        ref={isLast ? lastItemRef : undefined}
        data-testid={`nav-list-custom-item-${item.id}`}
        className={cn(
          'flex items-center gap-2 px-3 py-2.5 border-l-2 border-b border-neutral-100 transition-colors group cursor-pointer',
          isActive
            ? 'bg-primary-50 border-l-brand-600'
            : 'border-l-transparent hover:bg-neutral-25',
          isInactive && 'opacity-60'
        )}
      >
        {context?.setSelected && (
          <div
            className={cn('opacity-0 group-hover:opacity-100 shrink-0', {
              'opacity-100': isActive,
            })}
          >
            <Checkbox
              id={String(item.id)}
              data-testid={`nav-list-custom-checkbox-${item.id}`}
              checked={isActive}
              onCheckedChange={() => handleToggleSelected(item.id)}
              onClick={(e) => handleClick(e, i)}
            />
          </div>
        )}
        <label
          key={item.id}
          data-testid={`nav-list-custom-label-${item.id}`}
          onClick={() => _makeActive(item.id)}
          className="flex items-center justify-between w-full min-h-[28px] cursor-pointer text-sm min-w-0 gap-2"
        >
          <span
            className={cn(
              'truncate',
              isActive ? 'text-primary-900 font-medium' : 'text-neutral-900',
              {
                'line-through': item.is_active === false,
              }
            )}
          >
            {item.title}
          </span>
          <div className="flex items-center shrink-0">
            {isCompany && (
              <Tooltip content="Firemní verze">
                <Building16Regular className="ml-2 shrink-0 text-neutral-400" />
              </Tooltip>
            )}
            {isCommunity && (
              <Tooltip content="Komunitní verze">
                <People16Regular className="ml-2 shrink-0 text-neutral-400" />
              </Tooltip>
            )}
            {item.classifications && item.classifications.length > 1 && (
              <Tooltip content={`Zatříděno v: ${item.classifications.join(', ')}`}>
                <BranchFork16Regular className="ml-1 shrink-0 text-neutral-400" />
              </Tooltip>
            )}
            {item.is_working && (
              <Tooltip content="Rozpracovaná verze — zatím neschválená">
                <span className="shrink-0 ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border leading-none border-amber-400 text-amber-600">
                  Pracovní
                </span>
              </Tooltip>
            )}
            <StatusBadge item={item} />
          </div>
        </label>
      </div>
    );
  };

  const items: React.ReactNode[] = (data || []).map((item, i) =>
    renderItem(item, i, data.length === i + 1)
  );

  if (isFetching) {
    items.push(
      <div key="loading" className="flex justify-center py-3">
        <SpinnerOverlay />
      </div>
    );
  }

  return items;
};

export default NavListCustomInfinite;
